import { ofetch } from 'ofetch'
import type { FetchRequest, FetchOptions, FetchError } from 'ofetch'
import hash from 'object-hash'
import { TTLCache } from '../ttl-cache'
import type {
  ClientOptions,
  RequestInterceptor,
  ResponseInterceptor,
  AuthFn,
  ApiOptions,
  ApiMethods
} from './types'

const DEFAULT_API_OPTIONS = {
  useAuthorization: true,
  cache: false
} satisfies ApiOptions

const generateId = (() => {
  let id = 0

  return () => ++id
})()

export const createApiClient = (clientOptions: ClientOptions = {}) => {
  const apiFetch = ofetch.create({ baseURL: clientOptions.baseUrl })

  const cache = new TTLCache({ debug: false })
  const onRequestInterceptors = new Set<RequestInterceptor>()
  const onResponseInterceptors = new Set<ResponseInterceptor>()

  const removeRequestInterceptor = (fn: RequestInterceptor) =>
    onRequestInterceptors.delete(fn)
  const removeResponseInterceptor = (fn: ResponseInterceptor) =>
    onResponseInterceptors.delete(fn)

  let language = clientOptions.language
  const headers: Record<string, string> = {}
  let authorization: AuthFn | null = null

  const request = async <T>(
    url: FetchRequest,
    method: string,
    requestOptions: FetchOptions = {},
    apiOptions: ApiOptions = {}
  ): Promise<T> => {
    const mergedApiOptions = {
      ...DEFAULT_API_OPTIONS,
      ...apiOptions
    }

    const id = hash({
      url,
      method,
      language,
      query: requestOptions.query
    })

    if (mergedApiOptions.cache && cache.has(id)) {
      return cache.get(id)
    }

    return apiFetch<T, any>(url, {
      ...requestOptions,

      method,

      headers: {
        ...headers,
        ...requestOptions.headers
      },

      async onRequest({ request, options }) {
        // prettier-ignore
        if (language) {
          (options.headers as Record<string, string>)['accept-language'] =
            language
        }

        if (mergedApiOptions.useAuthorization) {
          await authorization?.({ request, options })
        }

        await requestOptions.onRequest?.({ request, options })

        onRequestInterceptors.forEach(fn => {
          fn({ id, request, options })
        })
      },

      async onResponse({ error, options, request, response }) {
        if (
          response.status >= 200 &&
          response.status < 399 &&
          mergedApiOptions.cache
        ) {
          cache.set(id, response._data, mergedApiOptions.cache)
        }

        await requestOptions.onResponse?.({
          error,
          options,
          request,
          response
        })

        onResponseInterceptors.forEach(fn => {
          fn({ id, error, options, request, response })
        })
      },

      async onResponseError(error) {
        await clientOptions.onResponseError?.(error, id)
        throw error
      }
    })
  }

  return {
    id: generateId(),

    addRequestInterceptor: (fn: RequestInterceptor) => {
      onRequestInterceptors.add(fn)
      return () => removeRequestInterceptor(fn)
    },

    addResponseInterceptor: (fn: ResponseInterceptor) => {
      onResponseInterceptors.add(fn)
      return () => removeResponseInterceptor(fn)
    },

    removeRequestInterceptor,
    removeResponseInterceptor,

    isApiError: <T>(err: any): err is FetchError<T> =>
      'request' in err || 'response' in err,

    setLanguage: (value: string) => {
      language = value
    },

    addHeader: (key: string, value: string) => {
      headers[key] = value
    },

    removeHeader: (key: string) => {
      delete headers[key]
    },

    get hasAuthorization() {
      return Boolean(authorization)
    },

    setAuthorization: (fn: AuthFn) => {
      authorization = fn
    },

    ...({
      get: (url, options?, apiOptions?) =>
        request(url, 'GET', options, apiOptions),

      post: (url, options?, apiOptions?) =>
        request(url, 'POST', options, apiOptions),

      put: (url, options?, apiOptions?) =>
        request(url, 'PUT', options, apiOptions),

      delete: (url, options?, apiOptions?) =>
        request(url, 'DELETE', options, apiOptions)
    } satisfies ApiMethods)
  }
}

export type ApiClient = ReturnType<typeof createApiClient>
